/**
 * Component   : VerifyOTP
 * Description : 6-Digit OTP Verification Screen
 */

import React, { useState, useRef, useEffect } from "react";
import {
    Box,
    Button,
    Grid,
    Typography,
    TextField,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import logo from "../../images/melplogo1.svg";
import backgroundImage1 from "../../images/bg2.svg";
import doctorImage from "../../images/melp-doctor-login-logo.svg";
import { callAPI, Method } from "../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";

const OTP_LENGTH = 6;

export const VerifyOTP: React.FC = () => {
    const { showMessage } = useSnackbar();
    const navigate = useNavigate();
    const [otp, setOtp] = useState<string[]>(new Array(OTP_LENGTH).fill(""));
    const [loading, setLoading] = useState(false);
    const [resendTimer, setResendTimer] = useState(30);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const location = useLocation();
    const phoneNumber = location.state?.phoneNumber;

    // Handle input change
    useEffect(() => {

        async function generateOtp() {
            const response: any = await callAPI("Otp/generate", Method.POST, {
                type: 1,
                phoneNumber,
            })
            if (response?.success) {
                showMessage("OTP sent successfully!", "success");
            }
        }
        if (phoneNumber) {
            generateOtp();
        }
    }, [phoneNumber]);
    const handleChange = (value: string, index: number) => {
        if (/^[0-9]?$/.test(value)) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);

            // Move to next input automatically
            if (value && index < OTP_LENGTH - 1) {
                inputRefs.current[index + 1]?.focus();
            }
        }
    };

    // Handle backspace
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    // Verify OTP
    const handleVerify = async () => {
        const enteredOtp = otp.join("");
        if (enteredOtp.length !== OTP_LENGTH) {
            showMessage("Please enter a valid 6-digit OTP", "warning");
            return;
        }
        setLoading(true);
        // Simulate API call
        const response: any = await callAPI('Otp/verify', Method.POST, {
            phoneNumber,
            code: enteredOtp,
        })
        if (response?.success) {
            setTimeout(() => {
                setLoading(false);
                showMessage("OTP Verified Successfully!", "success");
                navigate('/forgot-password', { state: { phoneNumber } });
            }, 100);
        }
    };
    async function generateOtp() {
        const response: any = await callAPI("Otp/generate", Method.POST, {
            type: 1,
            phoneNumber,
        })
        if (response?.success) {
            showMessage("OTP sent successfully!", "success");
        }
    }

    // Resend OTP
    // Resend OTP
    const handleResend = async () => {
        if (!phoneNumber) {
            showMessage("Phone number not found!", "warning");
            return;
        }

        try {
            setLoading(true);
            generateOtp()
        } catch (error) {
            console.error("Error resending OTP:", error);
            showMessage("Something went wrong while resending OTP.", "error");
        } finally {
            setLoading(false);
        }
    };


    // Timer countdown effect
    React.useEffect(() => {
        let timer: NodeJS.Timeout;
        if (resendTimer > 0) {
            timer = setTimeout(() => setResendTimer((prev) => prev - 1), 1000);
        }
        return () => clearTimeout(timer);
    }, [resendTimer]);

    return (
        <Grid
            container
            sx={{
                position: "relative",
                backgroundColor: "#022219",
                backgroundImage: `url(${backgroundImage1})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                minHeight: "100vh",
                justifyContent: "center",
                alignItems: "center",
                padding: 2,
            }}
        >
            <Box
                component="img"
                src={logo}
                alt="MELP Logo"
                sx={{
                    position: "absolute",
                    top: 24,
                    left: 54,
                    width: 100,
                    zIndex: 10,
                }}
            />

            <Box
                sx={{
                    backgroundColor: "white",
                    padding: "40px 30px",
                    borderRadius: "16px",
                    boxShadow: "0px 4px 15px rgba(0,0,0,0.2)",
                    maxWidth: "400px",
                    width: "100%",
                    textAlign: "center",
                }}
            >
                {/* <img src={doctorImage} height={100} alt="doctor" /> */}
                <Typography
                    variant="h5"
                    fontWeight="bold"
                    sx={{ color: "#098963", mt: 1 }}
                >
                    Verify OTP
                </Typography>
                <Typography variant="body2" color="#098963" sx={{ mb: 3 }}>
                    Please enter the 6-digit code sent to your phone
                </Typography>

                <Box display="flex" justifyContent="center" gap={1} mb={3}>
                    {otp.map((digit, index) => (
                        <TextField
                            key={index}
                            inputRef={(el) => (inputRefs.current[index] = el)}
                            value={digit}
                            onChange={(e) => handleChange(e.target.value, index)}
                            onKeyDown={(e: any) => handleKeyDown(e, index)}
                            variant="outlined"
                            inputProps={{
                                maxLength: 1,
                                style: {
                                    textAlign: "center",
                                    fontSize: "1.5rem",
                                    width: "1rem",
                                    height: "1rem",
                                    borderRadius: "8px",
                                },
                            }}
                        />
                    ))}
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Button
                            fullWidth
                            variant="outlined"
                            sx={{
                                borderColor: "#098963",
                                color: "#098963",
                                borderRadius: 2,
                            }}
                            onClick={() => navigate("/login")}
                        >
                            Cancel
                        </Button>
                    </Grid>
                    <Grid item xs={6}>
                        <Button
                            fullWidth
                            variant="contained"
                            sx={{
                                backgroundColor: "#098963",
                                color: "white",
                                borderRadius: 2,
                                "&:hover": { backgroundColor: "#066e4f" },
                            }}
                            onClick={handleVerify}
                            disabled={loading}
                        >
                            {loading ? "Verifying..." : "Verify"}
                        </Button>
                    </Grid>
                </Grid>

                <Box mt={3}>
                    {resendTimer > 0 ? (
                        <Typography variant="body2" color="textSecondary">
                            Resend OTP in <strong>{resendTimer}</strong> sec
                        </Typography>
                    ) : (
                        <Button
                            onClick={handleResend}
                            sx={{
                                textTransform: "none",
                                color: "#098963",
                                fontWeight: 600,
                                fontSize: "0.9rem",
                            }}
                        >
                            Resend OTP
                        </Button>
                    )}
                </Box>
            </Box>
        </Grid>
    );
};
